import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * How a question's options are presented:
 * - `chips` (default) — a row of selectable text chips.
 * - `dropdown` — a native select of the option text.
 * - `customdropdown` — a dropdown whose rows render custom content (e.g. a
 *   preview thumbnail beside a label) while still saving the option's value.
 */
export type AiPromptDisplay = 'dropdown' | 'chips' | 'customdropdown';

/**
 * A rich option: the `value` is saved as the answer, while `label` (and an
 * optional `thumbnail`) control how it's shown. A plain string is shorthand for
 * `{ value: s, label: s }`.
 */
export interface AiPromptOption {
  /** The value stored as the answer when this option is chosen. */
  value: string;
  /** The text shown to the user (e.g. a filename). */
  label: string;
  /** Optional preview image URL, shown beside the label in a custom dropdown. */
  thumbnail?: string;
}

/** An option as authored: a plain string, or a rich {@link AiPromptOption}. */
export type AiPromptOptionInput = string | AiPromptOption;

/**
 * Resolves a question's suggested options. Either a static list, or a function
 * of the answers given so far (so a question can depend on an earlier one —
 * e.g. defaulting the mockup to the one belonging to the chosen page).
 */
export type AiPromptOptions =
  | AiPromptOptionInput[]
  | ((answers: Record<string, string>) => AiPromptOptionInput[]);

/** A single question in an AI prompt. Users may always type a custom answer. */
export interface AiPromptQuestion {
  /** Stable key used to store the answer (e.g. 'url', 'mockup'). */
  key: string;
  /** The question shown to the user. */
  text: string;
  /** Suggested answers (static, or derived from prior answers). */
  options: AiPromptOptions;
  /** How the options are presented. Defaults to `chips`. */
  display?: AiPromptDisplay;
  /** Optional helper line shown under the options (e.g. "…or type one below"). */
  hint?: string;
  /**
   * Only ask this question when it returns true for the answers so far (e.g. an
   * exclusions question that only applies to "All pages"). Skipped questions get
   * no answer.
   */
  when?: (answers: Record<string, string>) => boolean;
}

/** A selectable AI action — title, description, and the questions it asks. */
export interface AiPrompt {
  /** Optional stable id (e.g. 'basic'). */
  id?: string;
  /** Material icon name shown on the option card (e.g. 'auto_awesome'). */
  icon?: string;
  /**
   * Accent color for the option card — drives the icon badge gradient and the
   * hover highlight. Any CSS color; defaults to the magenta brand color.
   */
  color?: string;
  title: string;
  description: string;
  questions: AiPromptQuestion[];
}

/** Emitted when the user completes a prompt: the prompt plus answers by key. */
export interface AiPromptSubmission {
  prompt: AiPrompt;
  answers: Record<string, string>;
}

/** One entry in the chat thread. */
interface ChatMessage {
  role: 'ai' | 'user';
  text: string;
}

/**
 * Floating AI assistant: a robot button fixed bottom-right that opens a chat
 * panel. Choosing a prompt starts a chat thread — the assistant asks its
 * questions one at a time; the user answers by tapping a suggested option or
 * typing a custom reply in the input bar. When every question is answered it
 * emits `runPrompt` with the collected answers.
 *
 * Generic and data-driven: the host supplies the prompts (and their options),
 * so it owns any domain wiring (e.g. populating URLs/mockups from a round).
 */
@Component({
  selector: 'fb-ai-assistant',
  imports: [MatIconModule],
  templateUrl: './ai-assistant.html',
  styleUrl: './ai-assistant.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistant {
  /** The available AI actions to offer. */
  readonly prompts = input<AiPrompt[]>([]);

  /** Assistant display name in the panel header. */
  readonly assistantName = input('AI Assistant');

  /** The assistant's opening line shown when the panel is first opened. */
  readonly greeting = input('Hi! I\'m your AI assistant for Feedback. How would you like to proceed?');

  /** Emitted when the user finishes answering a prompt's questions. */
  readonly runPrompt = output<AiPromptSubmission>();

  /** Brief "typing" pause before each assistant reply, to feel chat-like. */
  private static readonly RESPONSE_DELAY_MS = 450;

  private readonly thread = viewChild<ElementRef<HTMLElement>>('thread');
  private readonly draftInput = viewChild<ElementRef<HTMLTextAreaElement>>('draftInput');

  /** Largest the draft input grows before it starts scrolling. */
  private static readonly MAX_DRAFT_LINES = 10;

  protected readonly open = signal(false);
  protected readonly selected = signal<AiPrompt | null>(null);
  protected readonly messages = signal<ChatMessage[]>([]);
  /** Index of the question currently awaiting an answer. */
  protected readonly step = signal(0);
  protected readonly answers = signal<Record<string, string>>({});
  protected readonly draft = signal('');
  protected readonly thinking = signal(false);
  /** Whether the custom-dropdown menu for the current question is open. */
  protected readonly customOpen = signal(false);

  /** The question awaiting an answer, or null when none/finished. */
  protected readonly currentQuestion = computed<AiPromptQuestion | null>(() => {
    const prompt = this.selected();
    if (!prompt) {
      return null;
    }
    const index = this.step();
    return index < prompt.questions.length ? prompt.questions[index] : null;
  });

  /**
   * Suggested options for the current question, resolved against the answers and
   * normalized to {@link AiPromptOption} (plain strings become `{value, label}`).
   */
  protected readonly currentOptions = computed<AiPromptOption[]>(() => {
    const question = this.currentQuestion();
    if (!question) {
      return [];
    }
    const resolved =
      typeof question.options === 'function' ? question.options(this.answers()) : question.options;
    return resolved.map((option) =>
      typeof option === 'string' ? { value: option, label: option } : option,
    );
  });

  /** How the current question's options should be presented (defaults to chips). */
  protected readonly currentDisplay = computed<AiPromptDisplay>(
    () => this.currentQuestion()?.display ?? 'chips',
  );

  /** True while the assistant is waiting on the user (input/chips enabled). */
  protected readonly awaitingAnswer = computed(
    () => !this.thinking() && this.currentQuestion() !== null,
  );

  constructor() {
    // Keep the newest message in view as the thread grows.
    effect(() => {
      this.messages();
      this.thinking();
      const element = this.thread()?.nativeElement;
      if (element) {
        setTimeout(() => (element.scrollTop = element.scrollHeight));
      }
    });

    // Put the cursor back in the input each time the assistant replies with a
    // question. The input is disabled while the assistant "types", which drops its
    // focus; without this the user has to click back into it after every reply.
    // Deferred so it runs once the input has been re-enabled in the DOM.
    effect(() => {
      if (!this.open() || !this.awaitingAnswer()) {
        return;
      }
      const element = this.draftInput()?.nativeElement;
      if (element) {
        setTimeout(() => element.focus());
      }
    });

    // Grow the draft input to fit the message as it's typed, and shrink it back
    // once the draft is sent/cleared. Reads the draft signal so it re-runs on
    // every change (including programmatic resets).
    effect(() => {
      this.draft();
      const element = this.draftInput()?.nativeElement;
      if (element) {
        this.autoGrowDraft(element);
      }
    });
  }

  /**
   * Resizes the draft textarea to fit its content so the whole message stays
   * visible, capped at MAX_DRAFT_LINES lines — beyond that it scrolls.
   */
  private autoGrowDraft(textarea: HTMLTextAreaElement): void {
    const style = getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 18;
    const verticalPadding =
      (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const verticalBorder =
      (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    const maxHeight = lineHeight * AiAssistant.MAX_DRAFT_LINES + verticalPadding + verticalBorder;

    // Reset first so the textarea can shrink, then size to its content height.
    textarea.style.height = 'auto';
    const desiredHeight = textarea.scrollHeight + verticalBorder;
    textarea.style.height = `${Math.min(desiredHeight, maxHeight)}px`;
    textarea.style.overflowY = desiredHeight > maxHeight ? 'auto' : 'hidden';
  }

  protected openPanel(): void {
    this.resetThread();
    this.messages.set([{ role: 'ai', text: this.greeting() }]);
    this.open.set(true);
  }

  protected close(): void {
    this.open.set(false);
    this.resetThread();
  }

  protected selectPrompt(prompt: AiPrompt): void {
    this.selected.set(prompt);
    this.answers.set({});
    this.step.set(0);
    this.messages.update((thread) => [...thread, { role: 'user', text: prompt.title }]);
    this.askCurrent();
  }

  /** Abandons the current prompt and returns to the option list. */
  protected chooseAnother(): void {
    this.selected.set(null);
    this.answers.set({});
    this.step.set(0);
    this.thinking.set(false);
    this.customOpen.set(false);
    this.messages.update((thread) => [
      ...thread,
      { role: 'ai', text: 'No problem — choose another option below.' },
    ]);
  }

  /**
   * Records an answer and advances. `value` is stored as the answer; `displayText`
   * (when given) is what's shown in the chat — so a custom option can save a full
   * URL while the thread shows just its filename.
   */
  protected answer(value: string, displayText?: string): void {
    const stored = value.trim();
    const shown = (displayText ?? value).trim();
    const prompt = this.selected();
    const question = this.currentQuestion();
    if (!stored || !prompt || !question || this.thinking()) {
      return;
    }
    this.messages.update((thread) => [...thread, { role: 'user', text: shown }]);
    this.answers.update((current) => ({ ...current, [question.key]: stored }));
    this.draft.set('');
    this.customOpen.set(false);
    this.step.update((index) => index + 1);
    this.askCurrent();
  }

  /** Answers with a chosen option (saving its value, showing its label). */
  protected answerOption(option: AiPromptOption): void {
    this.answer(option.value, option.label);
  }

  /** Answers from a native `<select>` change event. */
  protected answerFromSelect(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const option = this.currentOptions().find((candidate) => candidate.value === value);
    if (option) {
      this.answerOption(option);
    }
  }

  protected toggleCustomDropdown(): void {
    this.customOpen.update((isOpen) => !isOpen);
  }

  protected sendDraft(): void {
    this.answer(this.draft());
  }

  /** Asks the current question — or, when none remain, confirms and emits. */
  private askCurrent(): void {
    const prompt = this.selected();
    if (!prompt) {
      return;
    }
    // Move past questions whose `when` says they don't apply to the answers so far.
    const answersSoFar = this.answers();
    let firstApplicableIndex = this.step();
    while (
      firstApplicableIndex < prompt.questions.length &&
      prompt.questions[firstApplicableIndex].when?.(answersSoFar) === false
    ) {
      firstApplicableIndex++;
    }
    this.step.set(firstApplicableIndex);
    const index = firstApplicableIndex;
    this.thinking.set(true);
    setTimeout(() => {
      // Bail if the user closed the panel or switched prompts meanwhile.
      if (this.selected() !== prompt) {
        return;
      }
      if (index < prompt.questions.length) {
        this.messages.update((thread) => [
          ...thread,
          { role: 'ai', text: prompt.questions[index].text },
        ]);
        this.thinking.set(false);
      } else {
        this.messages.update((thread) => [
          ...thread,
          { role: 'ai', text: `Thanks! Starting "${prompt.title}" now…` },
        ]);
        this.thinking.set(false);
        this.runPrompt.emit({ prompt, answers: { ...this.answers() } });
      }
    }, AiAssistant.RESPONSE_DELAY_MS);
  }

  private resetThread(): void {
    this.selected.set(null);
    this.messages.set([]);
    this.answers.set({});
    this.step.set(0);
    this.draft.set('');
    this.thinking.set(false);
    this.customOpen.set(false);
  }
}
